import { useMutation } from "@apollo/client";
import { Formik, FormikActions } from "formik";
import React, { useContext, useState } from "react";
import * as Yup from "yup";

import { DispatchContext } from "App";
import { BackButton, StyledGrid } from "components/simple";
import { ForgotPasswordFormValues } from "models/user";
import { ForgotPassword } from "graphql/user/user.gql";
import { ReactComponent as ForgotPasswordIcon } from "assets/forgot-password.svg";
import Confirmation from "./Confirmation";
import { RequestForm } from "./Forms";
import { color } from "style/constants";
import { Callout, Title } from "./styled";

const initialFormValues = {
  email: ""
};

const ForgotPasswordForm: React.FC = () => {
  const { dispatch } = useContext(DispatchContext);
  const [emailSent, setEmailSent] = useState(false);
  const [forgotPassword] = useMutation(ForgotPassword);
  const ForgotPasswordSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email")
  });

  const submitForm = async (
    values: ForgotPasswordFormValues,
    actions: FormikActions<ForgotPasswordFormValues>
  ): Promise<null> => {
    const { email } = values;
    try {
      const res = await forgotPassword({
        variables: { email: email }
      });
      if (
        res.data &&
        res.data.sendForgotPasswordEmail &&
        res.data.sendForgotPasswordEmail.success
      ) {
        setEmailSent(true);
      }
    } catch (err) {
      dispatch({ type: "error", description: "Check email" });
      return null;
    } finally {
      actions.setSubmitting(false);
    }
    return null;
  };

  return (
    <StyledGrid backgroundColor={color.WHITE} container direction="column" fillRemainingHeight>
      {emailSent ? (
        <Confirmation resend={() => setEmailSent(false)} />
      ) : (
        <>
          <BackButton />
          <StyledGrid
            container
            centerContent
            direction="row"
            maxWidth="1086px"
            spacing={10}
            margin="auto"
            fillRemainingHeight
          >
            <StyledGrid alignSelf="center" item maxWidth="470px">
              <ForgotPasswordIcon width="100%" height="100%" />
            </StyledGrid>
            <StyledGrid
              container
              direction="column"
              item
              lg={6}
              justifyContent="center"
              textAlign="left"
              spacing={3}
            >
              <StyledGrid item>
                <Title>Forgot Password</Title>
                <Callout>Enter your email address associated with your account below.</Callout>
              </StyledGrid>
              <StyledGrid item>
                <Formik
                  component={RequestForm}
                  initialValues={initialFormValues}
                  onSubmit={submitForm}
                  validationSchema={ForgotPasswordSchema}
                  validateOnBlur
                />
              </StyledGrid>
            </StyledGrid>
          </StyledGrid>
        </>
      )}
    </StyledGrid>
  );
};

export default ForgotPasswordForm;
